import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Upload, Table, Select, Steps, message, Button, Icon, Alert, Spin, Tag, Divider } from "antd";
import * as XLSX from 'xlsx/dist/xlsx.full.min.js';
import moment from "moment";

import * as bankReconciliationActions from "../actions";
import * as bancosActions from "~/screens/Financial/Bancos/actions";
import { systemIndex } from "./systemIndex";
import { parseAmount } from "./parseAmount";
import InputCurrency, { currency2Float, toBRL } from '~/components/UI/InputCurrency';

var Step = Steps.Step;
var Dragger = Upload.Dragger;
var Option = Select.Option;

// Render a raw preview cell formatting currency-looking values in BR locale
// ("11,300.00" / "815.24" -> "11.300,00" / "815,24"), leaving dates, plain text
// and bare integers untouched. Only values that clearly carry cents (a decimal
// separator followed by 1-2 digits) are reformatted, so dates like "02/01/2026"
// and thousands-only values like "1.500" are never touched.
var CURRENCY_CELL_RE = /^-?(\d{1,3}([.,]\d{3})*|\d+)[.,]\d{1,2}$/;
var formatPreviewCell = function(cell) {
    if (cell === null || cell === undefined) return '';
    var str = String(cell).trim();
    if (CURRENCY_CELL_RE.test(str)) {
        return toBRL(parseAmount(str));
    }
    return String(cell);
};

// ─── String utilities ─────────────────────────────────────────────────────────

// Trim, normalize Unicode (NFC) and strip invisible/control characters
var cleanString = function(value) {
    if (value === null || value === undefined) return '';
    var str = String(value).trim();
    // Normalize to NFC (composed form) — avoids duplicate accented-char representations
    if (str.normalize) {
        str = str.normalize('NFC');
    }
    // Remove C0/C1 control chars except TAB (09), LF (0A), CR (0D)
    str = str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F\x80-\x9F]/g, '');
    // Remove zero-width and other invisible Unicode spaces
    str = str.replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '');
    return str.trim();
};

// Decode XML/HTML entities that some OFX generators embed in text fields
var decodeHtmlEntities = function(str) {
    if (!str) return str;
    return str
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&#(\d+);/g, function(match, num) {
            return String.fromCharCode(parseInt(num, 10));
        });
};

// Detect the best text encoding for an OFX file buffer.
// Brazilian banks typically declare CHARSET:1252 (Windows-1252) or USASCII
// (which they often use as an alias for Windows-1252 in practice).
var detectOFXEncoding = function(buffer) {
    var bytes = new Uint8Array(buffer);

    // UTF-8 BOM: EF BB BF
    if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
        return 'utf-8';
    }

    // Read first 1 KB as plain ASCII to locate the OFX envelope headers
    var limit = Math.min(bytes.length, 1024);
    var headerStr = '';
    for (var i = 0; i < limit; i++) {
        headerStr += String.fromCharCode(bytes[i]);
    }

    var encodingMatch = headerStr.match(/ENCODING:([^\r\n<]+)/i);
    var charsetMatch  = headerStr.match(/CHARSET:([^\r\n<]+)/i);
    var encoding = encodingMatch ? encodingMatch[1].trim().toUpperCase() : '';
    var charset  = charsetMatch  ? charsetMatch[1].trim()               : '';

    if (encoding === 'UTF-8' || encoding.indexOf('UTF') > -1) {
        return 'utf-8';
    }
    // CHARSET:1252 → Windows-1252
    if (charset === '1252') {
        return 'windows-1252';
    }
    // Explicit ISO-8859-1 declaration (CHARSET:28591 is the IANA code for it)
    if (encoding === 'ISO-8859-1' || charset === '28591') {
        return 'iso-8859-1';
    }
    // USASCII/ASCII declarations are commonly used by Brazilian banks but the
    // files contain Windows-1252 encoded bytes — default to windows-1252.
    return 'windows-1252';
};

// Decode an OFX file buffer to a string.
// Strategy: try strict UTF-8 first (will throw on invalid byte sequences),
// then fall back to the encoding declared in the OFX header, then windows-1252.
// This handles the common case where Brazilian banks write ENCODING:USASCII in
// the header but actually produce UTF-8 content (e.g. "DISPONÍVEL").
var decodeOFXBuffer = function(buffer) {
    var bytes = new Uint8Array(buffer);

    // 1. UTF-8 BOM: always UTF-8
    if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
        return new TextDecoder('utf-8').decode(buffer);
    }

    // 2. Try strict UTF-8 — if the content is valid UTF-8 (even if declared as USASCII),
    //    use it.  { fatal: true } makes TextDecoder throw on invalid byte sequences.
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    } catch (e) {
        // Not valid UTF-8 — fall through to header-declared encoding
    }

    // 3. Use the encoding declared in the OFX envelope header
    var declaredEncoding = detectOFXEncoding(buffer);
    try {
        return new TextDecoder(declaredEncoding).decode(buffer);
    } catch (e) {
        // Unknown label, fall through
    }

    // 4. Last resort: windows-1252, then raw Latin-1 byte mapping
    try {
        return new TextDecoder('windows-1252').decode(buffer);
    } catch (e) {
        var str = '';
        for (var i = 0; i < bytes.length; i++) {
            str += String.fromCharCode(bytes[i]);
        }
        return str;
    }
};

// ─── OFX parser ───────────────────────────────────────────────────────────────

var parseOFX = function(content) {
    var transactions = [];

    var getValue = function(block, tag) {
        var regex = new RegExp('<' + tag + '>([^<\\n\\r]+)');
        var match = block.match(regex);
        return match ? cleanString(decodeHtmlEntities(match[1])) : null;
    };

    // Extract balance from LEDGERBAL or AVAILBAL
    var balanceAmount = null;
    var balanceDate = null;
    var balMatch = content.match(/<LEDGERBAL>([\s\S]*?)(?:<\/LEDGERBAL>|<AVAILBAL|<\/STMTRS)/);
    if (!balMatch) balMatch = content.match(/<AVAILBAL>([\s\S]*?)(?:<\/AVAILBAL>|<\/STMTRS)/);
    if (balMatch) {
        var balBlock = balMatch[1];
        var balAmt = getValue(balBlock, 'BALAMT');
        var balDt = getValue(balBlock, 'DTASOF');
        if (balAmt) balanceAmount = parseAmount(balAmt) || null;
        if (balDt) balanceDate = moment(balDt.substring(0, 8), 'YYYYMMDD').format('YYYY-MM-DD');
    }

    var blocks = content.split('<STMTTRN>');
    blocks.shift();

    blocks.forEach(function(block) {
        var dtposted = getValue(block, 'DTPOSTED');
        var trnamt   = getValue(block, 'TRNAMT');
        var memo     = getValue(block, 'MEMO') || getValue(block, 'NAME') || '';
        var fitid    = getValue(block, 'FITID');
        var checknum = getValue(block, 'CHECKNUM');

        if (!dtposted || !trnamt) return;

        var date = moment(dtposted.substring(0, 8), 'YYYYMMDD');
        if (!date.isValid()) return;

        var amount = parseAmount(trnamt) || 0;
        if (amount === 0) return;

        transactions.push({
            transaction_date: date.format('YYYY-MM-DD'),
            description: memo,
            amount: Math.abs(amount),
            transaction_type: amount < 0 ? 'debit' : 'credit',
            document_number: checknum || fitid || null,
        });
    });

    return {
        transactions: transactions,
        balance: balanceAmount,
        balanceDate: balanceDate,
    };
};

var STEPS = {
    BANCO: 0,
    UPLOAD: 1,
    COLUNAS: 2,
    CONFIRMAR: 3,
    SALDO: 4,
};

var Import = function(props) {
    var visible = props.visible;
    var onCancel = props.onCancel;
    var onSuccess = props.onSuccess;

    var dispatch = useDispatch();

    var bancosData = useSelector(function(state) {
        return state.bancos && state.bancos.data || [];
    });

    var [selectedBankId, setSelectedBankId] = useState(null);
    var [fileList, setFileList] = useState([]);
    var [rawRows, setRawRows] = useState([]);
    var [headerRowIndex, setHeaderRowIndex] = useState(0);
    var [originalData, setOriginalData] = useState([]);
    var [data, setData] = useState([]);
    var [mapping, setMapping] = useState({});
    var [step, setStep] = useState(STEPS.BANCO);
    var [columns, setColumns] = useState([]);
    var [loading, setLoading] = useState(false);
    var [checkingDuplicates, setCheckingDuplicates] = useState(false);
    var [duplicateInfo, setDuplicateInfo] = useState(null);
    var [isOfx, setIsOfx] = useState(false);
    var [selectedRowKeys, setSelectedRowKeys] = useState([]);
    var [ofxBalance, setOfxBalance] = useState(null);
    var [ofxBalanceDate, setOfxBalanceDate] = useState(null);
    var [confirmedBalance, setConfirmedBalance] = useState(null);
    var [balanceConfirmed, setBalanceConfirmed] = useState(false);
    var [userBalance, setUserBalance] = useState(null);

    useEffect(function() {
        if (visible && bancosData.length === 0) {
            dispatch(bancosActions.loadBancos());
        }
    }, [visible]);

    var handleUpload = function(info) {
        var file = info.file;
        var fileName = file.name || '';
        var isOfxFile = fileName.toLowerCase().indexOf('.ofx') > -1;

        if (isOfxFile) {
            // Read as ArrayBuffer so we can detect encoding before decoding
            var reader = new FileReader();
            reader.onload = function(e) {
                var content = decodeOFXBuffer(e.target.result);
                var result = parseOFX(content);
                if (result.transactions.length === 0) {
                    message.error('Nenhuma transação encontrada no arquivo OFX.');
                    return;
                }
                setIsOfx(true);
                setData(result.transactions);
                setOriginalData(result.transactions);
                setRawRows([]);
                setOfxBalance(result.balance);
                setOfxBalanceDate(result.balanceDate);
            };
            reader.readAsArrayBuffer(file);
        } else {
            var xlsReader = new FileReader();
            xlsReader.onload = function(e) {
                // type:"array" is correct for ArrayBuffer input (type:"binary" expects a binary string)
                var workbook = XLSX.read(e.target.result, { type: "array", codepage: 1252, raw: false, dateNF: 'dd/mm/yyyy' });
                var sheet = workbook.Sheets[workbook.SheetNames[0]];
                var rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, dateNF: 'dd/mm/yyyy' });

                setIsOfx(false);
                setRawRows(rows);

                // Auto-detect first non-empty row as default header
                var firstNonEmpty = 0;
                for (var i = 0; i < rows.length; i++) {
                    if (rows[i] && rows[i].some(function(c) {
                        return c !== null && c !== undefined && String(c).trim() !== '';
                    })) {
                        firstNonEmpty = i;
                        break;
                    }
                }
                setHeaderRowIndex(firstNonEmpty);
            };
            xlsReader.readAsArrayBuffer(file);
        }

        setFileList([file]);
    };

    var swapKeyValue = function(obj) {
        var swapped = {};
        Object.keys(obj).forEach(function(key) { swapped[obj[key]] = key; });
        return swapped;
    };

    var handleMappingChange = function(value, key) {
        var updated = {};
        Object.keys(mapping).forEach(function(k) { updated[k] = mapping[k]; });
        updated[key] = value;
        setMapping(updated);
    };

    var applyMapping = function() {
        var mappingSwapped = swapKeyValue(mapping);
        return originalData.map(function(row) {
            var mappedRow = {};
            Object.keys(row).forEach(function(fieldKey) {
                var systemKey = mappingSwapped[fieldKey];
                if (systemKey) mappedRow[systemKey] = row[fieldKey];
            });
            return mappedRow;
        });
    };

    // parseAmount is imported from ./parseAmount (extracted so it can be unit
    // tested across the locale/format matrix — see parseAmount.test.js).

    var parseDate = function(value) {
        if (!value) return null;
        var formats = ['DD/MM/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY', 'MM/DD/YYYY'];
        for (var i = 0; i < formats.length; i++) {
            var parsed = moment(value, formats[i], true);
            if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
        }
        var d = new Date(value);
        if (!isNaN(d.getTime())) return moment(d).format('YYYY-MM-DD');
        return null;
    };

    // Returns all parsed statements (used for duplicate check and table display)
    var getAllStatements = function() {
        if (isOfx) {
            return data.filter(function(row) { return row.transaction_date && row.amount; });
        }
        return applyMapping().map(function(row) {
            var amount = parseAmount(row.amount);
            return {
                transaction_date: parseDate(row.transaction_date),
                description: cleanString(row.description || ''),
                amount: Math.abs(amount),
                transaction_type: amount < 0 ? 'debit' : 'credit',
                document_number: row.document_number ? cleanString(row.document_number) : null,
                balance: row.balance ? parseAmount(row.balance) : null,
            };
        }).filter(function(row) { return row.transaction_date && row.amount; });
    };

    // Returns selected keys excluding "SALDO TOTAL" rows
    var selectAllExcludingSaldo = function(stmts) {
        return stmts.map(function(_, i) { return i; }).filter(function(i) {
            var desc = (stmts[i].description || '').toUpperCase();
            return desc.indexOf('SALDO TOTAL') === -1;
        });
    };

    // Returns only the user-selected rows (used for the actual import)
    var getStatementsForSubmit = function() {
        var all = getAllStatements();
        return all.filter(function(_, i) { return selectedRowKeys.indexOf(i) > -1; });
    };

    // When a row is unchecked, look for other rows with the same description
    // that are still checked and offer to bulk-uncheck them.
    var handleRowSelectionChange = function(newKeys) {
        // Detect which rows were just unchecked
        var uncheckedKeys = selectedRowKeys.filter(function(k) {
            return newKeys.indexOf(k) === -1;
        });

        // Apply the change immediately so the UI feels responsive
        setSelectedRowKeys(newKeys);

        // Nothing unchecked (user checked a row) → nothing more to do
        if (uncheckedKeys.length === 0) return;

        var allStmts = getAllStatements();

        // Collect the unique non-empty descriptions that were just unchecked
        var uncheckedDescs = [];
        uncheckedKeys.forEach(function(k) {
            var desc = allStmts[k] && allStmts[k].description ? allStmts[k].description.trim() : '';
            if (desc && uncheckedDescs.indexOf(desc) === -1) {
                uncheckedDescs.push(desc);
            }
        });

        if (uncheckedDescs.length === 0) return;

        // Find still-checked rows that share one of those descriptions
        var siblings = newKeys.filter(function(k) {
            var desc = allStmts[k] && allStmts[k].description ? allStmts[k].description.trim() : '';
            return uncheckedDescs.indexOf(desc) > -1;
        });

        if (siblings.length === 0) return;

        // Keys that would remain after bulk-unchecking siblings
        var keysAfterBulk = newKeys.filter(function(k) {
            return siblings.indexOf(k) === -1;
        });

        var label = uncheckedDescs.length === 1
            ? ('"' + (uncheckedDescs[0].length > 50 ? uncheckedDescs[0].substring(0, 50) + '…' : uncheckedDescs[0]) + '"')
            : (uncheckedDescs.length + ' descrições');

        Modal.confirm({
            title: 'Desmarcar registros similares?',
            content: (
                <span>
                    Encontramos mais <strong>{siblings.length}</strong> registro{siblings.length > 1 ? 's' : ''} com a mesma descrição {label}. Desmarcar todos?
                </span>
            ),
            okText: 'Desmarcar todos',
            cancelText: 'Não',
            onOk: function() {
                setSelectedRowKeys(keysAfterBulk);
            },
        });
    };

    var runDuplicateCheck = function(statements, onDone) {
        setCheckingDuplicates(true);
        dispatch(bankReconciliationActions.checkDuplicates(statements))
            .then(function(response) {
                setDuplicateInfo(response.payload.data.data);
                setCheckingDuplicates(false);
                onDone();
            })
            .catch(function() {
                setCheckingDuplicates(false);
                onDone();
            });
    };

    var countDataRows = function() {
        return rawRows.slice(headerRowIndex + 1).filter(function(r) {
            return r && r.some(function(c) {
                return c !== null && c !== undefined && String(c).trim() !== '';
            });
        }).length;
    };

    var buildJsonFromRawRows = function() {
        var headers = rawRows[headerRowIndex] || [];
        var json = [];
        rawRows.slice(headerRowIndex + 1).forEach(function(row) {
            var hasData = row && row.some(function(c) {
                return c !== null && c !== undefined && cleanString(String(c)) !== '';
            });
            if (!hasData) return;
            var obj = {};
            headers.forEach(function(header, i) {
                var rawHeader = header !== null && header !== undefined ? cleanString(String(header)) : '';
                var colName = rawHeader || ('Col ' + (i + 1));
                var cellVal = row[i] !== null && row[i] !== undefined ? cleanString(String(row[i])) : '';
                obj[colName] = cellVal;
            });
            json.push(obj);
        });
        return json;
    };

    var handleNext = function() {
        if (step === STEPS.BANCO) {
            if (!selectedBankId) {
                message.warning('Selecione uma conta bancária para continuar');
                return;
            }
            setStep(STEPS.UPLOAD);
            return;
        }

        if (step === STEPS.UPLOAD) {
            if (isOfx) {
                if (!data || !data.length) {
                    message.warning('Selecione um arquivo para continuar');
                    return;
                }
                runDuplicateCheck(getAllStatements(), function() {
                    var all = getAllStatements();
                    setSelectedRowKeys(selectAllExcludingSaldo(all));
                    setStep(STEPS.CONFIRMAR);
                });
                return;
            }

            if (!rawRows || !rawRows.length) {
                message.warning('Selecione um arquivo para continuar');
                return;
            }
            if (countDataRows() === 0) {
                message.warning('Nenhuma linha de dados encontrada após o cabeçalho selecionado.');
                return;
            }

            var json = buildJsonFromRawRows();
            var headers = rawRows[headerRowIndex] || [];
            var cols = headers.map(function(h, i) {
                return (h !== null && h !== undefined && String(h).trim())
                    ? String(h).trim() : ('Col ' + (i + 1));
            });

            var autoMapping = {};
            Object.keys(systemIndex).forEach(function(key) {
                var label = systemIndex[key].label.toLowerCase().replace(/\s/g, '');
                var match = cols.find(function(col) {
                    return col.toLowerCase().replace(/\s/g, '') === label;
                });
                if (match) autoMapping[key] = match;
            });

            setOriginalData(json);
            setData(json);
            setColumns(cols);
            setMapping(autoMapping);
            setStep(STEPS.COLUNAS);
            return;
        }

        if (step === STEPS.COLUNAS) {
            var requiredFields = Object.keys(systemIndex).filter(function(key) {
                return systemIndex[key].required;
            });
            var missingFields = requiredFields.filter(function(key) { return !mapping[key]; });
            if (missingFields.length > 0) {
                message.warning('Campos obrigatórios não mapeados: ' + missingFields.map(function(key) {
                    return systemIndex[key].label;
                }).join(', '));
                return;
            }

            var mappedData = applyMapping();
            setData(mappedData);

            var stmts = mappedData.map(function(row) {
                var amount = parseAmount(row.amount);
                return {
                    transaction_date: parseDate(row.transaction_date),
                    description: row.description || '',
                    amount: Math.abs(amount),
                    transaction_type: amount < 0 ? 'debit' : 'credit',
                    document_number: row.document_number || null,
                };
            }).filter(function(row) { return row.transaction_date && row.amount; });

            runDuplicateCheck(stmts, function() {
                setSelectedRowKeys(selectAllExcludingSaldo(stmts));
                setStep(STEPS.CONFIRMAR);
            });
            return;
        }
    };

    var handleBack = function() {
        if (step === STEPS.SALDO) {
            setStep(STEPS.CONFIRMAR);
        } else if (step === STEPS.CONFIRMAR && isOfx) {
            setStep(STEPS.UPLOAD);
        } else {
            setStep(step - 1);
        }
    };

    var handleSubmit = function() {
        setLoading(true);
        var statements = getStatementsForSubmit();
        if (statements.length === 0) {
            message.error('Nenhum registro válido encontrado.');
            setLoading(false);
            return;
        }

        var fileName = fileList[0] ? fileList[0].name : 'extrato_' + moment().format('YYYY-MM-DD_HH-mm-ss') + '.xlsx';
        dispatch(bankReconciliationActions.importBankStatements({
            statements: statements,
            file_name: fileName,
            bank_account_id: selectedBankId,
        })).then(function(response) {
            setLoading(false);
            resetModal();
            var result = response.payload.data.data;
            var imported = result.imported || 0;
            var skipped = result.skipped || 0;
            if (imported === 0 && skipped > 0) {
                message.warning('Nenhum registro importado. Todos os ' + skipped + ' registros já existem.');
            } else if (skipped > 0) {
                message.success(imported + ' registros importados. ' + skipped + ' duplicados ignorados.');
            } else {
                message.success(imported + ' registros importados com sucesso!');
            }
            if (onSuccess) onSuccess();
        }).catch(function() {
            message.error('Erro ao importar extrato');
            setLoading(false);
        });
    };

    var resetModal = function() {
        setStep(STEPS.BANCO);
        setSelectedBankId(null);
        setFileList([]);
        setRawRows([]);
        setHeaderRowIndex(0);
        setOriginalData([]);
        setData([]);
        setMapping({});
        setColumns([]);
        setDuplicateInfo(null);
        setCheckingDuplicates(false);
        setIsOfx(false);
        setSelectedRowKeys([]);
        setUserBalance(null);
        setBalanceConfirmed(false);
    };

    var handleCancel = function() {
        resetModal();
        if (onCancel) onCancel();
    };

    // ─── Render helpers ────────────────────────────────────────────────────────

    var renderStepBanco = function() {
        var selectedBank = selectedBankId
            ? bancosData.find(function(b) { return b.id === selectedBankId; })
            : null;

        return (
            <div style={{ padding: '16px 0 8px' }}>
                <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <div style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: '#e6f7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px',
                    }}>
                        <Icon type="bank" style={{ fontSize: 28, color: '#1890ff' }} />
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                        Selecione a conta bancária
                    </div>
                    <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>
                        Escolha a conta para qual este extrato pertence
                    </div>
                </div>

                <div style={{ maxWidth: 480, margin: '0 auto' }}>
                    {bancosData.length === 0 ? (
                        <Alert
                            type="warning"
                            showIcon
                            message="Nenhum banco cadastrado"
                            description={
                                <span>
                                    Cadastre uma conta bancária antes de importar.{' '}
                                    <a href="/financeiro/bancos" target="_blank">Ir para Bancos</a>
                                </span>
                            }
                        />
                    ) : (
                        <div>
                            <Select
                                showSearch
                                optionFilterProp="children"
                                size="large"
                                style={{ width: '100%' }}
                                placeholder="Buscar conta bancária..."
                                value={selectedBankId || undefined}
                                onChange={function(value) { setSelectedBankId(value); }}
                            >
                                {bancosData.map(function(bank) {
                                    return (
                                        <Option key={bank.id} value={bank.id}>
                                            {bank.name}
                                            {bank.bank_code ? ' (' + bank.bank_code + ')' : ''}
                                            {bank.account_number ? ' — Conta ' + bank.account_number : ''}
                                        </Option>
                                    );
                                })}
                            </Select>

                            {selectedBank && (
                                <div style={{
                                    marginTop: 16,
                                    padding: '14px 18px',
                                    background: '#f6ffed',
                                    border: '1px solid #b7eb8f',
                                    borderRadius: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                }}>
                                    <Icon type="check-circle" theme="filled" style={{ color: '#52c41a', fontSize: 22, flexShrink: 0 }} />
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedBank.name}</div>
                                        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 2 }}>
                                            {selectedBank.agency ? 'Ag. ' + selectedBank.agency : ''}
                                            {selectedBank.agency && selectedBank.account_number ? ' · ' : ''}
                                            {selectedBank.account_number ? 'Conta ' + selectedBank.account_number : ''}
                                            {!selectedBank.agency && !selectedBank.account_number ? 'Conta selecionada' : ''}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    var handleRemoveFile = function() {
        setFileList([]);
        setRawRows([]);
        setHeaderRowIndex(0);
        setData([]);
        setOriginalData([]);
        setIsOfx(false);
    };

    var renderStepUpload = function() {
        var hasFile = fileList.length > 0;
        var visibleRows = rawRows.slice(0, 20);
        var maxCols = 0;
        visibleRows.forEach(function(row) { if (row && row.length > maxCols) maxCols = row.length; });
        var displayCols = Math.min(maxCols, 8);
        var dataRowCount = countDataRows();

        var attachedFile = hasFile ? fileList[0] : null;
        var fileExt = attachedFile ? (attachedFile.name || '').split('.').pop().toLowerCase() : '';
        var fileIcon = fileExt === 'ofx' ? 'file-text' : 'file-excel';
        var fileSizeLabel = attachedFile && attachedFile.size
            ? (attachedFile.size > 1024 * 1024
                ? (attachedFile.size / (1024 * 1024)).toFixed(1) + ' MB'
                : Math.round(attachedFile.size / 1024) + ' KB')
            : '';

        return (
            <div style={{ padding: '8px 0' }}>
                {!hasFile ? (
                    <Dragger
                        beforeUpload={function() { return false; }}
                        onChange={handleUpload}
                        fileList={[]}
                        accept=".xlsx,.xls,.csv,.ofx"
                        style={{ borderRadius: 8 }}
                    >
                        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                            <Icon type="cloud-upload" style={{ fontSize: 40, color: '#1890ff' }} />
                        </p>
                        <p className="ant-upload-text" style={{ fontSize: 15, fontWeight: 500 }}>
                            Clique ou arraste o arquivo aqui
                        </p>
                        <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                            Suporta Excel (.xlsx, .xls), CSV (.csv) e OFX (.ofx)
                        </p>
                    </Dragger>
                ) : (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '14px 18px',
                        background: '#f6ffed',
                        border: '1px solid #b7eb8f',
                        borderRadius: 8,
                    }}>
                        <Icon
                            type={fileIcon}
                            style={{ fontSize: 28, color: fileExt === 'ofx' ? '#1890ff' : '#52c41a', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                fontWeight: 600,
                                fontSize: 13,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {attachedFile.name}
                            </div>
                            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 2 }}>
                                {fileExt.toUpperCase()}
                                {fileSizeLabel ? ' · ' + fileSizeLabel : ''}
                                {isOfx && data.length > 0 ? ' · ' + data.length + ' transações' : ''}
                                {!isOfx && dataRowCount > 0 ? ' · ' + dataRowCount + ' linhas de dados' : ''}
                            </div>
                        </div>
                        <Button
                            size="small"
                            icon="close"
                            onClick={handleRemoveFile}
                            title="Remover arquivo"
                        />
                    </div>
                )}

                {rawRows.length > 0 && !isOfx && (
                    <div style={{ marginTop: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontWeight: 500, fontSize: 13 }}>
                                <Icon type="table" style={{ marginRight: 6, color: '#1890ff' }} />
                                Selecionar linha de cabeçalho
                            </span>
                            <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                                Clique na linha que contém os nomes das colunas
                            </span>
                        </div>

                        <div style={{ border: '1px solid #e8e8e8', borderRadius: 6, overflow: 'hidden' }}>
                            <div style={{ overflowX: 'auto', maxHeight: 220, overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                                    <tbody>
                                        {visibleRows.map(function(row, rowIdx) {
                                            var isHeader = rowIdx === headerRowIndex;
                                            var isAbove = rowIdx < headerRowIndex;
                                            return (
                                                <tr
                                                    key={rowIdx}
                                                    onClick={function() { setHeaderRowIndex(rowIdx); }}
                                                    style={{
                                                        cursor: 'pointer',
                                                        background: isHeader ? '#e6f7ff' : isAbove ? '#fffbe6' : '#fff',
                                                        borderBottom: '1px solid ' + (isHeader ? '#91d5ff' : '#f0f0f0'),
                                                    }}
                                                >
                                                    <td style={{
                                                        padding: '5px 8px',
                                                        width: 90,
                                                        minWidth: 90,
                                                        borderRight: '1px solid #e8e8e8',
                                                        textAlign: 'center',
                                                        fontSize: 11,
                                                        whiteSpace: 'nowrap',
                                                        color: isHeader ? '#1890ff' : isAbove ? '#faad14' : 'rgba(0,0,0,0.35)',
                                                        fontWeight: isHeader ? 600 : 400,
                                                    }}>
                                                        {isHeader ? (
                                                            <span>
                                                                <Icon type="tag" theme="filled" style={{ marginRight: 3 }} />
                                                                cabeçalho
                                                            </span>
                                                        ) : isAbove ? (
                                                            <span>ignorar</span>
                                                        ) : (
                                                            <span>linha {rowIdx + 1}</span>
                                                        )}
                                                    </td>
                                                    {(row || []).slice(0, displayCols).map(function(cell, colIdx) {
                                                        return (
                                                            <td key={colIdx} style={{
                                                                padding: '5px 10px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                borderRight: '1px solid #f5f5f5',
                                                                fontWeight: isHeader ? 600 : 400,
                                                                color: isAbove ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.85)',
                                                                maxWidth: 140,
                                                            }}>
                                                                {formatPreviewCell(cell)}
                                                            </td>
                                                        );
                                                    })}
                                                    {(row || []).length > displayCols && (
                                                        <td style={{ padding: '5px 8px', color: 'rgba(0,0,0,0.3)', fontSize: 11, whiteSpace: 'nowrap' }}>
                                                            +{(row || []).length - displayCols} cols
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                        {rawRows.length > 20 && (
                                            <tr>
                                                <td colSpan={displayCols + 2} style={{ padding: '6px 10px', textAlign: 'center', color: 'rgba(0,0,0,0.35)', fontSize: 11, background: '#fafafa' }}>
                                                    +{rawRows.length - 20} linhas não exibidas
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'space-between' }}>
                            <span>
                                <Icon type="info-circle" style={{ marginRight: 4 }} />
                                Linha {headerRowIndex + 1} como cabeçalho
                                {headerRowIndex > 0 && (
                                    <span style={{ marginLeft: 6, color: '#faad14' }}>
                                        ({headerRowIndex} {headerRowIndex === 1 ? 'linha ignorada' : 'linhas ignoradas'})
                                    </span>
                                )}
                            </span>
                            <span style={{ color: dataRowCount > 0 ? 'rgba(0,0,0,0.45)' : '#f5222d', fontWeight: 500 }}>
                                {dataRowCount} linhas de dados
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    var renderStepColunas = function() {
        return (
            <div style={{ padding: '8px 0' }}>
                <Alert
                    type="info"
                    showIcon
                    message="Associe as colunas do arquivo com os campos do sistema"
                    description="Os campos marcados com * são obrigatórios. A coluna de exemplo mostra o primeiro valor encontrado."
                    style={{ marginBottom: 16 }}
                />
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#fafafa', borderBottom: '2px solid #e8e8e8' }}>
                            <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500, fontSize: 12, color: 'rgba(0,0,0,0.45)', width: '28%' }}>
                                CAMPO DO SISTEMA
                            </th>
                            <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500, fontSize: 12, color: 'rgba(0,0,0,0.45)', width: '38%' }}>
                                COLUNA NO ARQUIVO
                            </th>
                            <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500, fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                                EXEMPLO
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(systemIndex).map(function(key) {
                            var isRequired = systemIndex[key].required;
                            var mappedCol = mapping[key];
                            var sampleValue = mappedCol && originalData[0] ? originalData[0][mappedCol] : null;
                            var isMapped = !!mappedCol;
                            return (
                                <tr key={key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '10px 14px', fontSize: 13 }}>
                                        <span style={{ fontWeight: isRequired ? 500 : 400 }}>
                                            {systemIndex[key].label}
                                        </span>
                                        {isRequired && <span style={{ color: '#f5222d', marginLeft: 3, fontSize: 12 }}>*</span>}
                                    </td>
                                    <td style={{ padding: '8px 14px' }}>
                                        <Select
                                            style={{ width: '100%' }}
                                            onChange={function(value) { handleMappingChange(value, key); }}
                                            placeholder="Selecione uma coluna..."
                                            value={mappedCol || undefined}
                                            allowClear
                                            size="small"
                                        >
                                            {columns.map(function(col) {
                                                return <Option key={col} value={col}>{col}</Option>;
                                            })}
                                        </Select>
                                    </td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: sampleValue ? 'rgba(0,0,0,0.65)' : 'rgba(0,0,0,0.2)' }}>
                                        {isMapped ? (sampleValue || '—') : '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    var renderStepConfirmar = function() {
        var tableColumns = [
            { title: 'Data', dataIndex: 'transaction_date', key: 'transaction_date', width: 100 },
            {
                title: 'Descrição',
                dataIndex: 'description',
                key: 'description',
                ellipsis: true,
                render: function(val) {
                    return <span title={val}>{val}</span>;
                },
            },
            {
                title: 'Valor',
                dataIndex: 'amount',
                key: 'amount',
                width: 120,
                align: 'right',
                render: function(val, record) {
                    var isCredit = record.transaction_type === 'credit';
                    return (
                        <span style={{ color: isCredit ? '#52c41a' : '#f5222d', fontWeight: 600 }}>
                            {isCredit ? '+' : '-'}{toBRL(val)}
                        </span>
                    );
                },
            },
            { title: 'Doc.', dataIndex: 'document_number', key: 'document_number', width: 100, render: function(val) { return val || '-'; } },
        ];

        if (checkingDuplicates) {
            return (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16, color: 'rgba(0,0,0,0.45)' }}>Verificando duplicados...</p>
                </div>
            );
        }

        var allStatements = getAllStatements();
        if (allStatements.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <Icon type="inbox" style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#8c8c8c', marginBottom: 8 }}>Nenhuma transação encontrada</div>
                    <div style={{ fontSize: 13, color: '#bfbfbf' }}>O arquivo importado não contém transações válidas. Volte e selecione outro arquivo.</div>
                </div>
            );
        }
        var selectedCount = selectedRowKeys.length;
        var ignoredCount = allStatements.length - selectedCount;
        var dupCount = duplicateInfo ? duplicateInfo.duplicates : 0;

        var selectedCredits = allStatements.filter(function(s, i) {
            return selectedRowKeys.indexOf(i) > -1 && s.transaction_type === 'credit';
        }).length;
        var selectedDebits = allStatements.filter(function(s, i) {
            return selectedRowKeys.indexOf(i) > -1 && s.transaction_type === 'debit';
        }).length;

        var rowSelection = {
            selectedRowKeys: selectedRowKeys,
            onChange: handleRowSelectionChange,
            columnWidth: 36,
        };

        return (
            <div style={{ padding: '8px 0' }}>
                {duplicateInfo && dupCount > 0 && dupCount < (duplicateInfo.total || 0) && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message={
                            <span>
                                <strong>{dupCount}</strong> de <strong>{duplicateInfo.total}</strong> registros já existem e serão ignorados na importação.
                            </span>
                        }
                    />
                )}
                {duplicateInfo && dupCount > 0 && dupCount === (duplicateInfo.total || 0) && (
                    <Alert
                        type="error"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Todos os registros já foram importados anteriormente."
                        description="Nenhum registro novo será adicionado."
                    />
                )}

                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                    <div style={{ flex: 1, padding: '10px 14px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, textAlign: 'center' }}>
                        <Icon type="arrow-up" style={{ fontSize: 14, color: '#52c41a', marginBottom: 4 }} />
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#52c41a' }}>{selectedCredits}</div>
                        <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', marginTop: 1 }}>Créditos</div>
                    </div>
                    <div style={{ flex: 1, padding: '10px 14px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 8, textAlign: 'center' }}>
                        <Icon type="arrow-down" style={{ fontSize: 14, color: '#f5222d', marginBottom: 4 }} />
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#f5222d' }}>{selectedDebits}</div>
                        <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', marginTop: 1 }}>Débitos</div>
                    </div>
                    <div style={{ flex: 1, padding: '10px 14px', background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 8, textAlign: 'center' }}>
                        <Icon type="check-circle" style={{ fontSize: 14, color: '#722ed1', marginBottom: 4 }} />
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#722ed1' }}>{selectedCount}</div>
                        <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', marginTop: 1 }}>Selecionados</div>
                    </div>
                    {ignoredCount > 0 && (
                        <div style={{ flex: 1, padding: '10px 14px', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 8, textAlign: 'center' }}>
                            <Icon type="eye-invisible" style={{ fontSize: 14, color: 'rgba(0,0,0,0.35)', marginBottom: 4 }} />
                            <div style={{ fontSize: 20, fontWeight: 700, color: 'rgba(0,0,0,0.45)' }}>{ignoredCount}</div>
                            <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.35)', marginTop: 1 }}>Ignorados</div>
                        </div>
                    )}
                </div>

                {(function() {
                    var saldoCount = allStatements.filter(function(s) { return (s.description || '').toUpperCase().indexOf('SALDO TOTAL') > -1; }).length;
                    if (saldoCount === 0) return null;
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 10, background: '#fff7e6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 12, color: '#ad6800' }}>
                            <Icon type="warning" style={{ fontSize: 14, flexShrink: 0 }} />
                            <span>{saldoCount} linha{saldoCount > 1 ? 's' : ''} de <strong>SALDO TOTAL</strong> {saldoCount > 1 ? 'foram desmarcadas' : 'foi desmarcada'} automaticamente. Esses registros não representam transações reais.</span>
                        </div>
                    );
                })()}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                        <Icon type="info-circle" style={{ marginRight: 4 }} />
                        Desmarque as linhas que não devem ser importadas
                    </span>
                    <span style={{ fontSize: 12 }}>
                        <Button
                            size="small"
                            type="link"
                            style={{ padding: '0 4px' }}
                            onClick={function() {
                                setSelectedRowKeys(selectAllExcludingSaldo(allStatements));
                            }}
                        >
                            Marcar todos
                        </Button>
                        <span style={{ color: '#d9d9d9' }}>|</span>
                        <Button
                            size="small"
                            type="link"
                            style={{ padding: '0 4px' }}
                            onClick={function() { setSelectedRowKeys([]); }}
                        >
                            Desmarcar todos
                        </Button>
                    </span>
                </div>

                <Table
                    dataSource={allStatements}
                    columns={tableColumns}
                    rowKey={function(record, index) { return index; }}
                    rowSelection={rowSelection}
                    pagination={false}
                    size="small"
                    scroll={{ y: 280 }}
                    style={{ width: '100%' }}
                    rowClassName={function(record, index) {
                        return selectedRowKeys.indexOf(index) === -1 ? 'import-row-ignored' : '';
                    }}
                />
            </div>
        );
    };

    // ─── Step: Saldo ─────────────────────────────────────────────────────────

    var renderStepSaldo = function() {
        var stmts = getStatementsForSubmit();
        var totalCredits = 0;
        var totalDebits = 0;
        var minDate = null;
        var maxDate = null;

        stmts.forEach(function(s) {
            if (s.transaction_type === 'credit') totalCredits += s.amount;
            else totalDebits += s.amount;
            if (!minDate || s.transaction_date < minDate) minDate = s.transaction_date;
            if (!maxDate || s.transaction_date > maxDate) maxDate = s.transaction_date;
        });

        var periodText = minDate && maxDate
            ? moment(minDate).format('DD/MM/YYYY') + ' a ' + moment(maxDate).format('DD/MM/YYYY')
            : '-';
        var netAmount = totalCredits - totalDebits;

        return (
            <div style={{ padding: '8px 0' }}>
                <div style={{
                    borderRadius: 12, border: '1px solid #d6e4ff', background: '#fafcff',
                    overflow: 'hidden', marginBottom: 20,
                }}>
                    <div style={{
                        padding: '20px 24px', background: '#f0f5ff',
                        borderBottom: '1px solid #d6e4ff',
                        display: 'flex', alignItems: 'center', gap: 16,
                    }}>
                        <div style={{
                            width: 44, height: 44, borderRadius: 10, background: '#1890ff14',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Icon type="calculator" style={{ fontSize: 22, color: '#1890ff' }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: 15, color: '#262626' }}>Confirmar saldo do período</div>
                            <div style={{ fontSize: 13, color: '#8c8c8c' }}>Período: <strong>{periodText}</strong> &middot; {stmts.length} transações</div>
                        </div>
                    </div>

                    <div style={{ padding: '20px 24px' }}>
                        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                            <div style={{ flex: 1, padding: '12px 16px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, textAlign: 'center' }}>
                                <Icon type="arrow-up" style={{ fontSize: 14, color: '#52c41a', marginBottom: 4 }} />
                                <div style={{ fontSize: 18, fontWeight: 700, color: '#52c41a' }}>R$ {toBRL(totalCredits)}</div>
                                <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', marginTop: 2 }}>Total Créditos</div>
                            </div>
                            <div style={{ flex: 1, padding: '12px 16px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 8, textAlign: 'center' }}>
                                <Icon type="arrow-down" style={{ fontSize: 14, color: '#f5222d', marginBottom: 4 }} />
                                <div style={{ fontSize: 18, fontWeight: 700, color: '#f5222d' }}>R$ {toBRL(totalDebits)}</div>
                                <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', marginTop: 2 }}>Total Débitos</div>
                            </div>
                            <div style={{ flex: 1, padding: '12px 16px', background: netAmount >= 0 ? '#f6ffed' : '#fff1f0', border: '1px solid ' + (netAmount >= 0 ? '#b7eb8f' : '#ffa39e'), borderRadius: 8, textAlign: 'center' }}>
                                <Icon type="wallet" style={{ fontSize: 14, color: netAmount >= 0 ? '#52c41a' : '#f5222d', marginBottom: 4 }} />
                                <div style={{ fontSize: 18, fontWeight: 700, color: netAmount >= 0 ? '#52c41a' : '#f5222d' }}>R$ {toBRL(Math.abs(netAmount))}</div>
                                <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', marginTop: 2 }}>Saldo Líquido</div>
                            </div>
                        </div>

                        {/* Calculated balance */}
                        <div style={{
                            padding: '20px 24px', borderRadius: 10,
                            background: '#f6ffed', border: '1.5px solid #b7eb8f',
                            textAlign: 'center', marginBottom: ofxBalance !== null ? 12 : 0,
                        }}>
                            <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 6 }}>Saldo calculado do período</div>
                            <div style={{ fontSize: 28, fontWeight: 700, color: netAmount >= 0 ? '#52c41a' : '#f5222d' }}>
                                R$ {toBRL(Math.abs(netAmount))}
                            </div>
                            <div style={{ fontSize: 12, color: '#bfbfbf', marginTop: 6 }}>
                                Créditos (R$ {toBRL(totalCredits)}) - Débitos (R$ {toBRL(totalDebits)})
                            </div>
                        </div>

                        {ofxBalance !== null && (
                            <div style={{
                                padding: '12px 16px', borderRadius: 8,
                                background: '#f6f9fe', border: '1px solid #e8edf5',
                                display: 'flex', alignItems: 'center', gap: 12,
                            }}>
                                <Icon type="bank" style={{ fontSize: 15, color: '#1890ff' }} />
                                <span style={{ fontSize: 13, color: '#434343' }}>
                                    Saldo informado pelo banco (OFX): <strong>R$ {toBRL(Math.abs(ofxBalance))}</strong>
                                    {ofxBalanceDate ? ' em ' + moment(ofxBalanceDate).format('DD/MM/YYYY') : ''}
                                </span>
                            </div>
                        )}

                        {/* Optional override */}
                        <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 8, background: '#fafafa', border: '1px solid #e8e8e8' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <Icon type="edit" style={{ fontSize: 14, color: '#8c8c8c' }} />
                                <span style={{ fontSize: 13, color: '#434343', fontWeight: 500 }}>Saldo conforme extrato do banco:</span>
                                <InputCurrency
                                    size="small"
                                    style={{ width: 180 }}
                                    value={userBalance !== null ? userBalance : Math.abs(netAmount)}
                                    onChange={function(val) { setUserBalance(currency2Float(val)); }}
                                    placeholder="Informe o saldo"
                                />
                            </div>
                            {(function() {
                                var entered = userBalance !== null ? userBalance : Math.abs(netAmount);
                                var diff = Math.abs(entered - Math.abs(netAmount));
                                if (diff < 0.01) {
                                    return (
                                        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, fontSize: 13, color: '#389e0d' }}>
                                            <Icon type="check-circle" style={{ fontSize: 14 }} />
                                            <span>Saldo confere com o calculado. Tudo certo para importar!</span>
                                        </div>
                                    );
                                }
                                return (
                                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 13, color: '#ad6800' }}>
                                        <Icon type="warning" style={{ fontSize: 14, marginTop: 1 }} />
                                        <div>
                                            <div>Divergência de <strong>R$ {toBRL(diff)}</strong> entre o saldo informado e o calculado.</div>
                                            <div style={{ fontSize: 12, color: '#ad8b00', marginTop: 2 }}>Isso pode indicar transações faltando ou duplicadas no arquivo. Você ainda pode importar, mas recomendamos verificar.</div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ─── Footer ────────────────────────────────────────────────────────────────

    var isNextDisabled = (
        checkingDuplicates ||
        (step === STEPS.BANCO && bancosData.length === 0)
    );

    var footer = [
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
            Cancelar
        </Button>,
        step > STEPS.BANCO ? (
            <Button key="back" onClick={handleBack} disabled={loading || checkingDuplicates}>
                <Icon type="left" /> Voltar
            </Button>
        ) : null,
        <Button
            key="next"
            type="primary"
            loading={loading || checkingDuplicates}
            disabled={isNextDisabled}
            onClick={step === STEPS.SALDO ? handleSubmit : step === STEPS.CONFIRMAR ? function() { setStep(STEPS.SALDO); } : handleNext}
        >
            {step === STEPS.SALDO ? (
                <span><Icon type="import" style={{ marginRight: 4 }} />Importar</span>
            ) : step === STEPS.CONFIRMAR ? (
                <span>Próximo <Icon type="right" /></span>
            ) : (
                <span>Próximo <Icon type="right" /></span>
            )}
        </Button>,
    ];

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <Modal
            visible={visible}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon type="import" style={{ color: '#1890ff', fontSize: 18 }} />
                    <span style={{ fontWeight: 600 }}>Importar Extrato Bancário</span>
                </div>
            }
            onCancel={handleCancel}
            footer={footer.filter(Boolean)}
            width={860}
            bodyStyle={{ padding: '20px 24px' }}
        >
            <Steps current={isOfx && step > STEPS.UPLOAD ? step - 1 : step} size="small" style={{ marginBottom: 24 }}>
                <Step title="Banco" icon={<Icon type="bank" />} />
                <Step title="Upload" icon={<Icon type="upload" />} />
                {!isOfx && <Step title="Colunas" icon={<Icon type="table" />} />}
                <Step title="Confirmar" icon={<Icon type="check-circle" />} />
                <Step title="Saldo" icon={<Icon type="calculator" />} />
            </Steps>

            <div style={{ minHeight: 300 }}>
                {step === STEPS.BANCO && renderStepBanco()}
                {step === STEPS.UPLOAD && renderStepUpload()}
                {step === STEPS.COLUNAS && renderStepColunas()}
                {step === STEPS.CONFIRMAR && renderStepConfirmar()}
                {step === STEPS.SALDO && renderStepSaldo()}
            </div>
        </Modal>
    );
};

export default Import;
